<template>
  <div class="insight-config-dialog">
    <el-dialog
      v-model="isOpen"
      :show-close="false"
      width="600px"
      class="insight-config-drawer"
      title="Configurações do Agente de DRE"
      @open="handleSetup"
      :before-close="handleClose"
    >
      <template #header>
        <HeaderIaIcone
          title="Configurações do Agente de DRE"
          @close="handleClose"
        />
      </template>

      <div class="insight-config-drawer__content">
        <div class="insight-config-drawer__content-item">
          <div>
            <F360Select
              v-model="idModeloDRE"
              :options="modeloDREOptions"
              full
              :error="!idModeloDRE"
              placeholder="Selecione"
              label="Selecione o modelo de DRE"
            />
          </div>
          <div class="title">
            <p>
              <b>Escolha um dia do mês</b> para gerar o relatório automático
            </p>
          </div>
          <InsightsDiasDoMesSelect v-model="diaGeracaoRelatorio" />

          <div
            style="display: flex; justify-content: flex-end; margin-top: 0.5rem"
          >
            <F360Button
              v-if="tipo === ETipoConfiguracao.CONFIGURAR"
              :disabled="!diaGeracaoRelatorio || !idModeloDRE || loading"
              :loading="loading"
              filled
              @click="handleSalvar"
              >Salvar</F360Button
            >
          </div>
        </div>

        <div class="insight-config-drawer__content-item">
          <div class="title">
            <b>Adicione um número de WhatsApp</b> para ser
            <b>notificado</b> sempre que o relatório estiver disponível
            <span class="optional">(opcional)</span>
          </div>

          <div class="content">
            <F360Input
              v-model="phoneValue"
              placeholder="Digite o telefone"
              :error="!isValidBrazilianPhoneNumber(phoneValue)"
              full
              v-maska
              :data-maska="masks.PHONE"
            />

            <div class="actions" style="display: flex; gap: 0">
              <F360Button
                suffix-icon="check"
                class="button--confirm"
                :disabled="loading"
                :loading="loading"
                @click="handleSalvarTelefone"
              />
            </div>
          </div>
        </div>

        <InsightsConfiguracaoTermos
          v-if="tipo === ETipoConfiguracao.CONFIGURAR"
        />
      </div>

      <template #footer>
        <div class="insight-config-drawer__footer">
          <div>
            <PButton
              v-if="ativado"
              type="neutral"
              :disabled="!ativado"
              :loading="loading"
              @click="isDesativarDialogOpen = true"
            >
              Desativar agente
            </PButton>
          </div>
          <div>
            <F360Button
              v-if="tipo === ETipoConfiguracao.ATIVAR"
              :disabled="
                (tipo === ETipoConfiguracao.ATIVAR && !diaGeracaoRelatorio) ||
                !idModeloDRE ||
                loading
              "
              :loading="loading"
              filled
              @click="handleSalvar"
              >Ativar Agente</F360Button
            >
          </div>
        </div>
      </template>
    </el-dialog>
  </div>

  <InsightDesativarDialog
    v-model="isDesativarDialogOpen"
    @ativar="handleAtivar"
    @desativar="handleDesativar"
    :gateway="configuracaoGateway"
  />
</template>

<script setup lang="ts">
type _VTI_TYPE_ETipoConfiguracao = /* enum */ string
interface IProps {
  tipo: _VTI_TYPE_ETipoConfiguracao;
  gerarRelatorio: boolean;
}
import F360Button from '@/ui/components/Buttons/Button/F360Button.vue'
import InsightsDiasDoMesSelect from './components/InsightsDiasDoMesSelect.vue'
import { ref } from 'vue'
import HeaderIaIcone from '../../components/HeaderIaIcone.vue'
import InsightDesativarDialog from './widgets/desativar/InsightDesativarDialog.vue'
import { storeToRefs } from 'pinia'
import { diContainer } from '@/inversify.config'
import { InsightConfiguracaoGateway, InsightConfiguracaoGatewayDi } from './services/InsightConfiguracaoGateway'
import useAnalyticsInsightsIA, { AnalyticsInsightsIA } from '../../analytics/useInsightsIaAnalytics'
import { ETipoConfiguracao } from './types'
import InsightsConfiguracaoTermos from './components/InsightsConfiguracaoTermos.vue'
import { PButton } from '@f360-dev/f360-prisma'
import { useAgenteDeDreInsigthsStore } from '../../views/agenteDeDRE/store/useAgenteDeDreInsigthsStore.js'
import F360Input from '@/ui/components/Inputs/Input/F360Input.vue'
import { useMask } from '@/core/plugins/maska/useMask.js'
import useStorage from '@/core/plugins/useStorage.js'
import { DITypes, IUserProfile, LocalStorageKeys } from '@/core/@types/types.js'
import { IFinancasFeatureGateway, IUserProfilePayload } from '@/features/financas/services/ports/FinancasFeatureGateway.js'
import { isEmptyPhoneNumber, isValidBrazilianPhoneNumber } from '@/core/common/phoneUtils.js'
import F360Select from '@/ui/components/Select/Base/F360Select.vue'
const props = defineProps<IProps>();
const emit = defineEmits<{
  (e: "ativouAgente"): void;
}>();
const loading = ref(false);
const phoneValue = ref<string>("");
const { vMaska, masks } = useMask();
const isOpen = defineModel<boolean>({ default: false });
const isDesativarDialogOpen = ref(false);
const modeloDREOptions = ref<{ label: string; value: string }[]>([]);
const agenteDeDreInsigthsStore = useAgenteDeDreInsigthsStore();
const { ativado, diaGeracaoRelatorio, idModeloDRE } = storeToRefs(
  agenteDeDreInsigthsStore
);
const storage = useStorage();
const configuracaoGateway = diContainer.get<InsightConfiguracaoGateway>(
  InsightConfiguracaoGatewayDi
);
const financasGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);
const analyticsInsightsIA = useAnalyticsInsightsIA();
const handleAtivar = () => {
  isOpen.value = false;
  isDesativarDialogOpen.value = false;
};
const handleDesativar = async () => {
  try {
    const response = await configuracaoGateway.configurar({
      idModeloDRE: idModeloDRE.value || "",
      AgenteLigado: false,
      diaGeracaoRelatorio: diaGeracaoRelatorio.value || 10,
      WhatsappLigado: isValidBrazilianPhoneNumber(phoneValue.value)
        ? true
        : false
    });
    if (!response.isSuccess) {
      throw new Error("Erro ao desativar agente");
    } else {
      agenteDeDreInsigthsStore.setAtivado(false);
      agenteDeDreInsigthsStore.setProcessando(false);
      isOpen.value = false;
      isDesativarDialogOpen.value = false;
    }
  } catch (error) {
    console.error(error);
  }
};
const handleClose = () => {
  isOpen.value = false;
};
const handleAceitarTermo = async () => {
  try {
    analyticsInsightsIA.sendClickEvent(AnalyticsInsightsIA.ativar_agente);
    await agenteDeDreInsigthsStore.gateway.aceitarTermo({
      versaoTermo: agenteDeDreInsigthsStore.termoDeUso?.versao || "",
      urlDocumentoTermo: agenteDeDreInsigthsStore.termoDeUso?.url || ""
    });
  } catch (error) {
    console.error(error);
  } finally {
    emit("ativouAgente");
  }
};
const handleSalvar = async () => {
  analyticsInsightsIA.sendClickEvent(AnalyticsInsightsIA.salvar_data);
  if (!diaGeracaoRelatorio.value || !idModeloDRE.value) return;
  try {
    const response = await configuracaoGateway.configurar({
      idModeloDRE: idModeloDRE.value || "",
      diaGeracaoRelatorio: diaGeracaoRelatorio.value,
      AgenteLigado: true,
      WhatsappLigado: !isEmptyPhoneNumber(phoneValue.value)
    });
    if (response.isSuccess) {
      agenteDeDreInsigthsStore.setHasOptin(true);
      agenteDeDreInsigthsStore.setAtivado(true);
      agenteDeDreInsigthsStore.setProcessando(true);
      handleAceitarTermo();
      emit("ativouAgente");
      handleClose();
      agenteDeDreInsigthsStore.fetchGerarRelatorio();
      if (props.tipo === ETipoConfiguracao.ATIVAR) {
        agenteDeDreInsigthsStore.notificacao.showNotification(
          "Seu relat\u00f3rio est\u00e1 sendo gerado em background. Voc\u00ea ser\u00e1 avisado no celular quando estiver pronto ou pode aguardar nesta tela.",
          { type: "info" }
        );
      }
    }
  } catch (error) {
    console.error(error);
  }
};
const handleBuscarModelosDRE = async () => {
  try {
    loading.value = true;
    const response = await financasGateway.getModelosDREList();
    if (response?.Data) {
      modeloDREOptions.value =
        response.Data?.map((modelo) => ({
          label: modelo.Nome,
          value: modelo._id
        })) || [];
      if (!idModeloDRE.value && modeloDREOptions.value.length > 0)
        agenteDeDreInsigthsStore.setIdModeloDRE(
          modeloDREOptions.value[0].value
        );
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};
const handleSetup = async () => {
  await handleBuscarModelosDRE();
  const local = storage.get<IUserProfile>(LocalStorageKeys.UserProfile);
  if (local?.Telefone) {
    phoneValue.value = local.Telefone;
  }
};
const handleSalvarTelefone = async () => {
  try {
    loading.value = true;
    const local = storage.get<IUserProfile>(LocalStorageKeys.UserProfile);
    const payload: IUserProfilePayload = {
      Email: local?.Email || "",
      Franqueadores: local?.Franqueadores as [],
      Grupo: local?.Grupo || "",
      Nome: local?.Nome || "",
      PaginaInicial: local?.PaginaInicial || "",
      Perfil: local?.Perfil || 1,
      TemContabil: local?.TemContabil || false,
      TemPerformance: local?.TemPerformance || false,
      _id: local?._id || "",
      Telefone: phoneValue.value,
      isWhatsApp: isValidBrazilianPhoneNumber(phoneValue.value) ? true : false
    };
    const response = await financasGateway.updateUserProfile(payload);
    if (!response?.Ok) {
      console.log(response);
      agenteDeDreInsigthsStore.notificacao.showNotification(
        response?.Error || "Erro ao salvar telefone",
        {
          type: "error"
        }
      );
    }
    if (response?.Ok) {
      storage.set(LocalStorageKeys.UserProfile, {
        ...storage.get<IUserProfile>(LocalStorageKeys.UserProfile),
        Telefone: phoneValue.value,
        isWhatsApp: isValidBrazilianPhoneNumber(phoneValue.value) ? true : false
      });
      agenteDeDreInsigthsStore.notificacao.showNotification(
        "Telefone salvo com sucesso",
        {
          type: "success"
        }
      );
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss">
.insight-config-dialog {
  .financas-dialog {
    --financas-dialog-margin-top: 5vh;
    padding: 0;
  }

  .financas-dialog__header {
    padding-bottom: 0;
  }
}

.insight-config-drawer {
  .prisma-table--default .prisma-table__cell {
    padding: 4px 0 !important;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 16px;
    padding-top: 0;
    margin-top: 1rem;
    max-height: 470px;
    overflow-y: auto;

    &-item {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0 1rem 1rem;
      border-bottom: 1.5px solid _map.get($map: $neutro, $key: 100);

      .title {
        font-size: 14px;
        margin-bottom: 0rem;
      }

      .optional {
        font-size: 12px;
        color: _map.get($map: $neutro, $key: 500);
      }

      .subtitle {
        font-size: 12px;
        color: _map.get($map: $neutro, $key: 500);
      }

      .table-wrapper {
        :deep(.prisma-table--default .prisma-table__cell) {
          padding: 4px 0 !important;
        }
      }

      .content {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        background-color: _map.get($map: $neutro, $key: 50);
        padding: 1rem 1rem;
        gap: 1rem;

        .actions {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;

          .button--confirm {
            background-color: #05f;
            border: 1px solid #05f;
            color: #fff;
            opacity: 0.4;

            &:hover {
              opacity: 1;
            }
          }

          .button--cancel {
            color: #fff;
            background-color: #ff3032;
            border: 1px solid #ff3032;
            opacity: 0.4;

            &:hover {
              opacity: 1;
            }
          }
        }
      }
    }

    &-item:last-child {
      border-bottom: none;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid _map.get($map: $neutro, $key: 100);
    padding: 16px;
  }
}

@media (max-width: 768px) {
  .insight-config-drawer {
    width: 90%;

    &__content {
      &-item {
        padding: 0 0.5rem 0.5rem;
      }
    }

    &__footer {
      padding: 0.5rem;
    }
  }
}
</style>
